import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Search,
  Replace,
  ChevronUp,
  ChevronDown,
  CaseSensitive,
  Regex,
} from 'lucide-react';
import type { Editor } from '@tiptap/react';
import type { SearchMatch } from '../lib/search-highlight';

interface FindReplaceProps {
  editor: Editor | null;
  onClose: () => void;
}

interface PmMatch {
  pmFrom: number;
  pmTo: number;
  text: string;
}

/**
 * Build a flat string + position map from the ProseMirror document.
 * map[i] = the ProseMirror position that corresponds to character i in the flat string.
 */
function buildDocMap(editor: Editor): { flatText: string; map: number[] } {
  const flatChars: string[] = [];
  const map: number[] = [];

  editor.state.doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      for (let j = 0; j < node.text.length; j++) {
        flatChars.push(node.text[j]);
        // pos in ProseMirror is the position BEFORE the text node's first character
        map.push(pos + j);
      }
    }
    return true;
  });

  return { flatText: flatChars.join(''), map };
}

/**
 * Search for all matches inside the flat string and convert each
 * [from, to) offset pair to real ProseMirror positions using the map.
 */
function findPmMatches(
  editor: Editor,
  searchText: string,
  caseSensitive: boolean,
  useRegex: boolean,
): PmMatch[] {
  if (!searchText) return [];

  const { flatText, map } = buildDocMap(editor);
  if (!flatText || map.length === 0) return [];

  const results: PmMatch[] = [];

  try {
    if (useRegex) {
      const flags = caseSensitive ? 'g' : 'gi';
      const re = new RegExp(searchText, flags);
      let m: RegExpExecArray | null;
      while ((m = re.exec(flatText)) !== null) {
        const from = m.index;
        const to = m.index + m[0].length;
        if (to > from && from < map.length && to - 1 < map.length) {
          results.push({
            pmFrom: map[from],
            pmTo: map[to - 1] + 1,
            text: m[0],
          });
        }
        // Prevent infinite loop on zero-length match
        if (m[0].length === 0) re.lastIndex++;
      }
    } else {
      const needle = caseSensitive ? searchText : searchText.toLowerCase();
      const haystack = caseSensitive ? flatText : flatText.toLowerCase();
      let pos = 0;
      while (pos <= haystack.length - needle.length) {
        const idx = haystack.indexOf(needle, pos);
        if (idx === -1) break;
        const from = idx;
        const to = idx + needle.length;
        if (to - 1 < map.length) {
          results.push({
            pmFrom: map[from],
            pmTo: map[to - 1] + 1,
            text: flatText.substring(from, to),
          });
        }
        pos = idx + needle.length;
      }
    }
  } catch {
    // Invalid regex — return empty
    return [];
  }

  return results;
}

export function FindReplace({ editor, onClose }: FindReplaceProps) {
  const { t } = useTranslation();
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [showReplace, setShowReplace] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [matches, setMatches] = useState<PmMatch[]>([]);
  const [currentMatch, setCurrentMatch] = useState(-1);
  // Keep refs to the latest matches/current so keyboard handlers don't go stale
  const matchesRef = useRef<PmMatch[]>([]);
  const currentMatchRef = useRef(-1);

  /** Push current match list to the editor decoration plugin (no focus stolen). */
  const pushDecorations = useCallback(
    (found: PmMatch[], idx: number) => {
      if (!editor) return;
      const searchMatches: SearchMatch[] = found.map((m) => ({
        from: m.pmFrom,
        to: m.pmTo,
      }));
      editor.commands.setSearchMatches(searchMatches, idx);
    },
    [editor],
  );

  /** Clear all search decorations from the editor (called on close / unmount). */
  const clearDecorations = useCallback(() => {
    if (!editor || editor.isDestroyed) return;
    editor.commands.setSearchMatches([], -1);
  }, [editor]);

  const runSearch = useCallback(() => {
    if (!editor || !findText) {
      setMatches([]);
      matchesRef.current = [];
      setCurrentMatch(-1);
      currentMatchRef.current = -1;
      clearDecorations();
      return;
    }

    const found = findPmMatches(editor, findText, caseSensitive, useRegex);
    setMatches(found);
    matchesRef.current = found;

    const idx = found.length > 0 ? 0 : -1;
    setCurrentMatch(idx);
    currentMatchRef.current = idx;

    pushDecorations(found, idx);
  }, [editor, findText, caseSensitive, useRegex, pushDecorations, clearDecorations]);

  // Debounced search whenever inputs change
  useEffect(() => {
    const timer = setTimeout(runSearch, 200);
    return () => clearTimeout(timer);
  }, [runSearch]);

  // Clear decorations when the panel unmounts
  useEffect(() => {
    return () => {
      clearDecorations();
    };
  }, [clearDecorations]);

  /**
   * Scroll a match into view and update the "current" decoration highlight.
   * Never calls editor.focus() — focus stays in the search input.
   */
  const scrollToMatch = useCallback(
    (idx: number, found: PmMatch[]) => {
      if (!editor || found.length === 0 || idx < 0) return;

      // Update the current decoration
      pushDecorations(found, idx);

      // Scroll the match into the center of the visible area (best-effort)
      try {
        const { view } = editor;
        const match = found[idx];
        const coords = view.coordsAtPos(match.pmFrom);
        const parent =
          (view.dom.closest('[class*="overflow"]') as HTMLElement | null) ??
          view.dom.parentElement;
        if (parent) {
          const rect = parent.getBoundingClientRect();
          const offsetFromTop = coords.top - rect.top - rect.height / 2;
          parent.scrollBy({ top: offsetFromTop, behavior: 'smooth' });
        }
      } catch {
        // Scroll is best-effort
      }
    },
    [editor, pushDecorations],
  );

  const goToNext = useCallback(() => {
    const found = matchesRef.current;
    if (found.length === 0) return;
    const next = (currentMatchRef.current + 1) % found.length;
    currentMatchRef.current = next;
    setCurrentMatch(next);
    scrollToMatch(next, found);
  }, [scrollToMatch]);

  const goToPrevious = useCallback(() => {
    const found = matchesRef.current;
    if (found.length === 0) return;
    const prev = (currentMatchRef.current - 1 + found.length) % found.length;
    currentMatchRef.current = prev;
    setCurrentMatch(prev);
    scrollToMatch(prev, found);
  }, [scrollToMatch]);

  const replaceCurrent = useCallback(() => {
    if (!editor || matchesRef.current.length === 0 || currentMatchRef.current < 0) return;

    const match = matchesRef.current[currentMatchRef.current];
    // Insert content at the match position; do NOT call .focus() so the
    // search input keeps focus. Re-run search after DOM settles.
    editor.chain().insertContentAt({ from: match.pmFrom, to: match.pmTo }, replaceText).run();

    setTimeout(runSearch, 50);
  }, [editor, replaceText, runSearch]);

  const replaceAll = useCallback(() => {
    if (!editor || matchesRef.current.length === 0) return;

    // Iterate from the END to avoid offset drift
    const allMatches = [...matchesRef.current].reverse();
    const chain = editor.chain();
    for (const m of allMatches) {
      chain.insertContentAt({ from: m.pmFrom, to: m.pmTo }, replaceText);
    }
    chain.run();

    setTimeout(runSearch, 50);
  }, [editor, replaceText, runSearch]);

  // Close handler: clear highlights then invoke parent callback
  const handleClose = useCallback(() => {
    clearDecorations();
    onClose();
  }, [clearDecorations, onClose]);

  // Keyboard shortcuts (Escape / Enter / Shift+Enter / F3)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) {
          goToPrevious();
        } else {
          goToNext();
        }
      } else if (e.key === 'F3') {
        e.preventDefault();
        if (e.shiftKey) {
          goToPrevious();
        } else {
          goToNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, goToNext, goToPrevious]);

  const resultLabel =
    matches.length > 0
      ? t('findReplace.results', '{{current}} de {{total}}', {
          current: currentMatch + 1,
          total: matches.length,
        })
      : findText
      ? t('findReplace.noResults', 'Nenhum resultado')
      : t('findReplace.typeToSearch', 'Digite para buscar');

  return (
    <div className="w-full rounded-t-2xl border-t border-border bg-popover shadow-2xl sm:w-80 sm:rounded-xl sm:border sm:shadow-xl">
      <div className="mx-auto mt-2 mb-1 h-1.5 w-10 rounded-full bg-border sm:hidden" />
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowReplace(!showReplace)}
            className={`p-1.5 rounded transition-colors ${
              showReplace
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted'
            }`}
            title={t('findReplace.toggleReplace', 'Toggle Replace')}
          >
            <Replace size={16} />
          </button>
          <span className="text-sm font-medium text-foreground">
            {showReplace
              ? t('findReplace.findReplace', 'Find & Replace')
              : t('findReplace.find', 'Find')}
          </span>
        </div>
        <button
          onClick={handleClose}
          className="p-1.5 rounded text-muted-foreground hover:bg-muted"
        >
          <X size={16} />
        </button>
      </div>

      <div className="p-3 space-y-3">
        {/* Find Input */}
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={findText}
            onChange={(e) => setFindText(e.target.value)}
            placeholder={t('findReplace.searchPlaceholder', 'Search...')}
            className="w-full pl-9 pr-20 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            autoFocus
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <button
              onClick={() => setCaseSensitive(!caseSensitive)}
              className={`p-1 rounded transition-colors ${
                caseSensitive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              title={t('findReplace.caseSensitive', 'Case Sensitive')}
            >
              <CaseSensitive size={14} />
            </button>
            <button
              onClick={() => setUseRegex(!useRegex)}
              className={`p-1 rounded transition-colors ${
                useRegex
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              title={t('findReplace.regex', 'Use Regex')}
            >
              <Regex size={14} />
            </button>
          </div>
        </div>

        {/* Replace Input */}
        {showReplace && (
          <div className="relative">
            <Replace
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              placeholder={t('findReplace.replacePlaceholder', 'Replace with...')}
              className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            />
          </div>
        )}

        {/* Results & Navigation */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{resultLabel}</span>

          <div className="flex items-center gap-1">
            <button
              onClick={goToPrevious}
              disabled={matches.length === 0}
              className="p-1.5 rounded text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
              title={t('findReplace.previous', 'Previous (Shift+Enter)')}
            >
              <ChevronUp size={16} />
            </button>
            <button
              onClick={goToNext}
              disabled={matches.length === 0}
              className="p-1.5 rounded text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
              title={t('findReplace.next', 'Next (Enter)')}
            >
              <ChevronDown size={16} />
            </button>
          </div>
        </div>

        {/* Replace Actions */}
        {showReplace && (
          <div className="flex gap-2">
            <button
              onClick={replaceCurrent}
              disabled={matches.length === 0}
              className="flex-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('findReplace.replace', 'Replace')}
            </button>
            <button
              onClick={replaceAll}
              disabled={matches.length === 0}
              className="flex-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('findReplace.replaceAll', 'Replace All')}
            </button>
          </div>
        )}
      </div>

      {/* Keyboard hints — só desktop (no mobile não há teclado físico) */}
      <div className="hidden md:block px-3 py-2 border-t border-border bg-muted rounded-b-xl">
        <p className="text-[10px] text-muted-foreground text-center">
          Enter {t('findReplace.forNext', 'for next')} • Shift+Enter{' '}
          {t('findReplace.forPrevious', 'for previous')} • Esc{' '}
          {t('findReplace.toClose', 'to close')}
        </p>
      </div>
    </div>
  );
}
